import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { usePageHeader } from '@/contexts/PageHeaderContext';
import { Settings, BarChart3, Info, Clock, Target, Users, TrendingUp, CheckCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import ScoringAgentBasicInfo from '@/components/settings/scoring-agent/ScoringAgentBasicInfo';
import ScoringAgentQASettings from '@/components/settings/scoring-agent/ScoringAgentQASettings';
import ScoringAgentParameters from '@/components/settings/scoring-agent/ScoringAgentParameters';

const ScoringAgentDetail = () => {
  const { id } = useParams<{ id: string }>();
  const { setHeaderData } = usePageHeader();
  const [agentName, setAgentName] = useState<string>('Scoring Agent Detail');

  // Mock data - in real app, this would come from Redux store or API
  const mockScoringAgents = [
    {
      id: '1',
      name: 'Customer Support QA Agent',
      description: 'Quality assurance scoring agent for customer support interactions',
      teamName: 'Customer Support Team',
      teamId: 'team-1',
      status: 'live' as const,
      enabled: true,
      createdDate: '2024-01-15',
      lastModified: '2024-01-22',
      // QA Settings
      qaSettings: {
        delay: { value: 30, unit: 'minutes' as const },
        confidenceThreshold: 85,
        aiConfidenceThreshold: 75,
        frequency: { type: 'all' as const, value: 100 },
        ratingScale: '0-100' as const,
        acceptableThreshold: 70,
        scoreLabelBands: {
          allowHalfPoints: false,
          bands: [
            { id: '1', minScore: 0, maxScore: 49, label: 'Poor', color: '#ef4444' },
            { id: '2', minScore: 50, maxScore: 69, label: 'Below Average', color: '#f97316' },
            { id: '3', minScore: 70, maxScore: 84, label: 'Acceptable', color: '#eab308' },
            { id: '4', minScore: 85, maxScore: 94, label: 'Above Expectations', color: '#3b82f6' },
            { id: '5', minScore: 95, maxScore: 100, label: 'Exceeded Expectations', color: '#22c55e' }
          ]
        },
        supervisorNotifications: {
          scoreBelow: true,
          negativeParameters: false,
          selectedParameters: ['greeting', 'empathy'],
          method: 'in-app' as const
        }
      },
      // Scorecard weights
      agentParameters: [
        { name: 'Greeting', weight: 12.5, enabled: true, scale: 'Y/N' },
        { name: 'Empathy', weight: 12.5, enabled: true, scale: 'Y/N' },
        { name: 'Clarity', weight: 12.5, enabled: true, scale: '1–3' },
        { name: 'Solution Offered', weight: 12.5, enabled: true, scale: 'Y/N/N/A' },
        { name: 'Closing', weight: 12.5, enabled: true, scale: 'Y/N' },
        { name: 'Tone', weight: 12.5, enabled: true, scale: 'Positive/Neutral/Negative' },
        { name: 'Spelling & Grammar', weight: 12.5, enabled: true, scale: 'Error count or 1–3' },
        { name: 'Comprehension', weight: 12.5, enabled: true, scale: 'Y/N' }
      ],
      botParameters: [
        { name: 'Greeting/Opening', weight: 20, enabled: true, scale: 'Y/N' },
        { name: 'Clarity', weight: 20, enabled: true, scale: '1–3' },
        { name: 'Hand-off Effectiveness', weight: 20, enabled: true, scale: 'Y/N' },
        { name: 'Escalation as Needed', weight: 20, enabled: true, scale: 'Y/N' },
        { name: 'Tone', weight: 20, enabled: true, scale: 'Positive/Neutral/Negative' }
      ]
    },
    {
      id: '2', 
      name: 'Sales Team QA Agent',
      description: 'Quality assurance scoring agent for sales interactions',
      teamName: 'Sales Team',
      teamId: 'team-2',
      status: 'draft' as const,
      enabled: false,
      createdDate: '2024-01-20',
      lastModified: '2024-01-20',
      qaSettings: {
        delay: { value: 1, unit: 'hours' as const },
        confidenceThreshold: 80,
        aiConfidenceThreshold: 75,
        frequency: { type: 'percentage' as const, value: 50 },
        ratingScale: '1-10' as const,
        acceptableThreshold: 6,
        scoreLabelBands: {
          allowHalfPoints: true,
          bands: [
            { id: '1', minScore: 1.0, maxScore: 4.4, label: 'Poor', color: '#ef4444' },
            { id: '2', minScore: 4.5, maxScore: 6.4, label: 'Below Average', color: '#f97316' },
            { id: '3', minScore: 6.5, maxScore: 8.4, label: 'Acceptable', color: '#eab308' },
            { id: '4', minScore: 8.5, maxScore: 10.0, label: 'Exceeded Expectations', color: '#22c55e' }
          ]
        },
        supervisorNotifications: {
          scoreBelow: false,
          negativeParameters: true,
          selectedParameters: [],
          method: 'email' as const
        }
      },
      agentParameters: [
        { name: 'Greeting', weight: 25, enabled: true, scale: 'Y/N' },
        { name: 'Product Knowledge', weight: 25, enabled: true, scale: '1-3' },
        { name: 'Closing Technique', weight: 25, enabled: true, scale: 'Y/N' },
        { name: 'Follow-up', weight: 25, enabled: true, scale: 'Y/N' }
      ],
      botParameters: [
        { name: 'Lead Qualification', weight: 50, enabled: true, scale: 'Y/N' },
        { name: 'Information Capture', weight: 50, enabled: true, scale: '1-3' }
      ]
    },
    {
      id: '3',
      name: 'Product Team QA Agent',
      description: 'Quality assurance scoring agent for product support',
      teamName: 'Product Team',
      teamId: 'team-3', 
      status: 'live' as const,
      enabled: true,
      createdDate: '2024-01-10',
      lastModified: '2024-01-25',
      qaSettings: {
        delay: { value: 2, unit: 'days' as const },
        confidenceThreshold: 90,
        aiConfidenceThreshold: 85,
        frequency: { type: 'interval' as const, value: 5 },
        ratingScale: '1-5' as const,
        acceptableThreshold: 3,
        scoreLabelBands: {
          allowHalfPoints: true,
          bands: [
            { id: '1', minScore: 1.0, maxScore: 2.4, label: 'Poor', color: '#ef4444' },
            { id: '2', minScore: 2.5, maxScore: 3.4, label: 'Below Average', color: '#f97316' },
            { id: '3', minScore: 3.5, maxScore: 4.4, label: 'Acceptable', color: '#eab308' },
            { id: '4', minScore: 4.5, maxScore: 5.0, label: 'Exceeded Expectations', color: '#22c55e' }
          ]
        },
        supervisorNotifications: {
          scoreBelow: true,
          negativeParameters: true,
          selectedParameters: ['clarity', 'solution'],
          method: 'in-app' as const
        }
      },
      agentParameters: [
        { name: 'Technical Accuracy', weight: 30, enabled: true, scale: '1-3' },
        { name: 'Clarity', weight: 25, enabled: true, scale: '1-3' },
        { name: 'Solution Quality', weight: 25, enabled: true, scale: 'Y/N' },
        { name: 'Documentation', weight: 20, enabled: true, scale: 'Y/N' }
      ],
      botParameters: [
        { name: 'Feature Explanation', weight: 40, enabled: true, scale: '1-3' },
        { name: 'Documentation Links', weight: 30, enabled: true, scale: 'Y/N' },
        { name: 'Escalation Timing', weight: 30, enabled: true, scale: 'Y/N' }
      ]
    }
  ];

  useEffect(() => {
    // Find the agent by ID and set the name
    const agent = mockScoringAgents.find(agent => agent.id === id);
    const name = agent ? agent.name : 'Scoring Agent Detail';
    setAgentName(name);

    setHeaderData({
      title: agent ? agent.teamName : 'Scoring Agent Detail',
      description: 'View and manage scoring agent configuration',
      icon: Settings
    });

    return () => setHeaderData(null);
  }, [setHeaderData, id]);

  const currentAgent = mockScoringAgents.find(agent => agent.id === id);

  // Mock teams data - in real app would come from Redux
  const mockTeams = [
    { id: 'team-1', name: 'Customer Support Team' },
    { id: 'team-2', name: 'Sales Team' },
    { id: 'team-3', name: 'Product Team' }
  ];

  // Save handlers
  const handleBasicInfoSave = (data: any) => {
    console.log('Saving basic info:', data);
    // In real app: dispatch Redux action to update agent
  };

  const handleQASettingsSave = (settings: any) => {
    console.log('Saving QA settings:', settings);
    // In real app: dispatch Redux action to update agent QA settings
  };

  const handleParametersSave = (data: any) => {
    console.log('Saving parameters:', data);
    // In real app: dispatch Redux action to update agent parameters
  };

  return (
    <div className="p-6">
      <div className="max-w-5xl mx-auto space-y-6">
        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="overview" className="flex items-center gap-2">
              <Info className="h-4 w-4" />
              Details & Overview
            </TabsTrigger>
            <TabsTrigger value="analytics" className="flex items-center gap-2">
              <BarChart3 className="h-4 w-4" />
              Analytics
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-6">
            {currentAgent ? (
              <>
                {/* Basic Information - Now Editable */}
                <ScoringAgentBasicInfo
                  agent={{
                    id: currentAgent.id,
                    name: currentAgent.name,
                    description: currentAgent.description,
                    teamName: currentAgent.teamName,
                    teamId: currentAgent.teamId,
                    status: currentAgent.status,
                    enabled: currentAgent.enabled,
                    createdDate: currentAgent.createdDate,
                    lastModified: currentAgent.lastModified
                  }}
                  teams={mockTeams}
                  onSave={handleBasicInfoSave}
                />

                {/* QA Settings - Now Editable */}
                <ScoringAgentQASettings
                  qaSettings={currentAgent.qaSettings}
                  onSave={handleQASettingsSave}
                  agentParameters={currentAgent.agentParameters || []}
                  botParameters={currentAgent.botParameters || []}
                />

                {/* Scoring Parameters - Now Editable */}
                {(currentAgent.agentParameters?.length > 0 || currentAgent.botParameters?.length > 0) && (
                  <ScoringAgentParameters
                    agentParameters={currentAgent.agentParameters || []}
                    botParameters={currentAgent.botParameters || []}
                    onSave={handleParametersSave}
                  />
                )}
              </>
            ) : (
              <Card>
                <CardContent className="p-6">
                  <p className="text-muted-foreground">Scoring agent not found.</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="analytics" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Performance Analytics</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Analytics data and performance metrics for {agentName} will be displayed here.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Usage Statistics</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Usage statistics and charts will be displayed here.
                </p>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default ScoringAgentDetail;